import { BluetoothErrorCode } from '../../../../backbone-unorganized/utils/bluetooth';
import { isAndroid, isWeb, isWindows } from '../../../../shared/utils/is';
import { NumberFieldView } from '../numberField/views';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ReadingMethod } from '../constants';
import { showErrorMessage } from '../../../../shared/utils/notifications';

export const OilQualityFieldView = NumberFieldView.extend({
    template: '#tpl_oil_quality_field',
    model: null, // MobileOilQualityField
    is_polling_field: false, // keeps track of when the fake_input should be selected

    events: _.extend(
        {
            'click .fake_input': 'click_probe_input',
            'click .connected .bluetherm_indicator': 'poll_start',
            'click .disconnected .bluetherm_indicator': 'retry_connect'
        },
        NumberFieldView.prototype.events
    ),

    initialize: function (options) {
        OilQualityFieldView.__super__.initialize.apply(this, arguments);

        // Re-render on connection changes so the template swaps to the right button/input state.
        this.listenTo($f.application.vent, 'btdevice:connected btdevice:probe_head', () => {
            this.render();
        });

        this.listenTo($f.application.vent, 'btdevice:disconnected btdevice:connecting', () => {
            this.is_polling_field = false;
            this.render();
        });

        // The Saveris pushes a value only when its transmit button is pressed; there is no live
        // reading stream like the temperature probes have.
        this.listenTo($f.application.vent, 'btdevice:button_pressed', (value) => {
            if (!this.is_polling_field) {
                return;
            }
            this._set_value(value, ReadingMethod.PROBE);
        });

        this.listenTo(this.model, 'change:value', this.render);

        this.listenTo($f.application.vent, 'btdevice:clear_polling', (check_view) => {
            // exit if not polling field or if the view cid matches the currently active field
            if (!this.is_polling_field || this.cid === check_view.cid) {
                return;
            }
            // stop polling on any other fields
            this.poll_stop();
        });
    },

    templateHelpers: function () {
        const acceptanceValue = this.model.get('acceptance_value');
        let connection_state = 'disconnected';
        let placeholder_key = 'TPM';
        // The whole Bluetooth reading path (button, fake input, connection states) is behind the
        // testo_saveris_integration flag; off, this renders as a plain manual TPM entry.
        const bluetooth_enabled = this._is_bluetooth_enabled();
        const is_probe_connected = bluetooth_enabled && this._is_oil_probe_connected();

        if (bluetooth_enabled) {
            if ($f.mobile.bluetooth.is_connecting || $f.mobile.bluetooth.is_probe_head_pending()) {
                connection_state = 'connecting';
                placeholder_key = 'mobile_placeholder_blue_therm_connecting';
            } else if (this.is_polling_field) {
                connection_state = 'polling';
                placeholder_key = 'mobile_placeholder_testo_tap';
            } else if (is_probe_connected) {
                connection_state = 'connected';
                placeholder_key = 'mobile_placeholder_testo_tap';
            } else {
                placeholder_key = 'mobile_placeholder_blue_therm_not_connected';
            }
        }

        return $.extend(
            {
                acceptance_value_class: this.model.get('value') ? this.getAcceptanceCriteriaClass(acceptanceValue) : '',
                show_validation_message: this.showValidationMessage(acceptanceValue),
                value: this.model.get('value'),
                bluetooth_enabled: bluetooth_enabled,
                connection_state: connection_state,
                is_probe_connected: is_probe_connected,
                placeholder_text: _t(placeholder_key) || placeholder_key,
                is_force_probe_input: bluetooth_enabled && this._should_force_probe_input()
            },
            OilQualityFieldView.__super__.templateHelpers.apply(this, arguments)
        );
    },

    onRender: function () {
        // mounts mui icons
        if (this.model.get('acceptance_value') === -1) {
            const elem = this.$('.failure-alert')[0];
            window.mountReactJSX(elem, () => <ErrorOutlineIcon />);
        }
    },

    getAcceptanceCriteriaClass: function (acceptanceValue) {
        if (acceptanceValue === -1) {
            return 'failure';
        }

        if (acceptanceValue === 0) {
            return 'warning';
        }

        return 'neutral';
    },

    showValidationMessage: function (acceptanceValue) {
        if (acceptanceValue <= 0) return true;

        return false;
    },

    _should_force_probe_input: function () {
        if (isWindows() || isWeb()) {
            return false;
        }

        return this.model.collection.metadata.get('is_force_probe_input');
    },

    _is_bluetooth_enabled: function () {
        return $f.mobile.bluetooth.supports_bluetooth() && $f.flags.get_flag('testo_saveris_integration');
    },

    // Only the Saveris with its oil-quality head attached measures TPM; anything else renders
    // as "not connected".
    _is_oil_probe_connected: function () {
        const device = $f.mobile.bluetooth.device;

        return (
            $f.mobile.bluetooth.is_connected &&
            device &&
            device.get('type') === 'testo_saveris' &&
            device.get('probe_head') === 'oil'
        );
    },

    save_value: function (event) {
        // remove commas from input before saving
        event.target.value = event.target.value.replace(/,/g, '');
        this.fix_event_val_leading_decimal(event);

        const input = $(event.target).val();
        this.$el.removeClass('invalid');
        this.model.set({ value: input, reading_method: ReadingMethod.MANUAL });

        if (input != this.model.get('value')) {
            this.render();
        }
    },

    /**
     * The fake input has two behaviors: if we're already connected to a probe, start polling. If
     * not, is_force_probe_input is true, so let's try connecting to one.
     */
    click_probe_input: function (event) {
        $f.mobile.bluetooth.is_device_connected((is_connected) => {
            if (!is_connected) {
                this.retry_connect(event);
                return;
            }

            this.poll_start(event);
        });

        return false;
    },

    // TPM is a bare percentage — stored as-is, no unit conversion (unlike temperature).
    _set_value: function (value, readingMethod) {
        if (!value) {
            return;
        }

        this._scroll_down_one_field();
        this.model.set({ value: value, reading_method: readingMethod });
        this.poll_stop();
    },

    poll_start: function (event) {
        if (this.is_polling_field) {
            return false;
        }

        if (!$f.mobile.bluetooth.supports_bluetooth()) {
            return this._error_bluetooth_not_supported();
        }

        $f.mobile.bluetooth.is_device_connected((is_connected) => {
            if (!is_connected || !this._is_oil_probe_connected()) {
                this.poll_stop();
                return;
            }

            // stop polling other fields here
            $f.application.vent.trigger('btdevice:clear_polling', this);

            this.is_polling_field = true;

            this.render();
            // measurement_type tells the MF-Handle to stream oil quality (TPM), not temperature.
            $f.mobile.bluetooth.start_polling({
                measurement_type: 'oil_quality',
                error: (error_code) => {
                    if (error_code === BluetoothErrorCode.ERROR_PAIR_REQUEST_DENIED) {
                        $f.mobile.bluetooth.disconnect();
                    }

                    this.is_polling_field = false;
                    this.render();
                }
            });

            // Detect when the user clicks "out" of the fake_input
            $(window).on('click.oil_quality_stop_polling_' + this.model.get('id'), (_event) => {
                const target = $(_event.target);
                if (target.hasClass('bluetherm_indicator') || target.parents('.bluetherm_indicator').length > 0) {
                    return true;
                }
                this.poll_stop();
                return false;
            });
        });

        return false;
    },

    poll_stop: function (event) {
        $(window).off('click.oil_quality_stop_polling_' + this.model.get('id'));

        if (!this.is_polling_field) {
            return false;
        }

        this.is_polling_field = false;
        $f.mobile.bluetooth.stop_polling();
        this.render();
        return false;
    },

    retry_connect: function (event) {
        if (!$f.mobile.bluetooth.supports_bluetooth()) {
            this._error_bluetooth_not_supported();
            return false;
        }

        // On Android the BLE plugin removes focus from the webview, which can reload the form if
        // nothing's changed yet.
        if (isAndroid()) {
            this.trigger('prevent_next_reload_on_resume');
        }

        // Drops a connected probe whose head doesn't serve this field before scanning again.
        $f.mobile.bluetooth.reconnect();
        return false;
    },

    _error_bluetooth_not_supported: function () {
        if (isWeb()) {
            showErrorMessage(
                _t(
                    $f.flags.get_flag('rebrand')
                        ? 'mobile_alert_please_install_for_native'
                        : 'mobile_alert_please_install_for_native_old'
                )
            );
        } else {
            showErrorMessage(_t('mobile_windows_10_bluetherm_not_supported'));
        }
    },

    onClose: function () {
        $(window).off('click.oil_quality_stop_polling_' + this.model.get('id'));
    },

    /**
     * Move to the next visible field upon entering a value. So if this field generates a dependent text field, do nothing.
     *
     * Move to to the next visible field ONLY IF:
     *  - it is a oil quality field
     *  - and it has not been previously answered
     */
    on_change_action: function (model) {
        // Ensure the action is setting a new value, not clearing an existing one as well.
        if (model.changed.value === null) {
            return;
        }

        var next_model = model.get_next_visible();
        // Note: if an answer has been answered and then cleared it is null, but if it has never been touched,
        // the value is ""
        if (
            !next_model ||
            next_model.get('type') != 'oil_quality' ||
            ['', null].indexOf(next_model.get('value')) == -1
        ) {
            return;
        }

        var $target = $('#field_' + next_model.get('id'));
        if (!$target) {
            return;
        }
        var $input = $target.find('.fake_input');
        if (!$input) {
            return;
        }
        $input.focus().click();
        // after this sets a value, the delete box comes up which changes the height of the current element,
        // by delaying shortly we move down the full height of this element
        setTimeout(this._scroll_down_one_field.bind(this), 100);
    }
});
